import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Factory, Globe2, Wrench, Award, LifeBuoy, PhoneCall, MessageCircle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { waLink, quoteMessage } from "@/lib/whatsapp";
import WorldMap, { BranchDetailCard } from "@/components/WorldMap";
import TankPlaceholder from "@/components/TankPlaceholder";
import ClientsStrip from "@/components/ClientsStrip";

const HERO_IMG = "/tanks/pipeco-hero.webp";
const SECTOR_IMAGE_OVERRIDES = {
  "Water Treatment": "/sectors/water-treatment.webp",
  "Agriculture & Irrigation": "/sectors/agriculture-irrigation.webp",
  "Water & Utilities": "/sectors/water-utilities.webp",
  "Healthcare": "/sectors/healthcare.png",
  "Hotels & Hospitality": "/sectors/hotels-hospitality.webp",
  "Fire Protection": "/sectors/fire-protection.webp",
  "Infrastructure": "/sectors/infrastructure.webp",
  "Industrial": "/sectors/industrial.webp",
  "Commercial & Residential": "/sectors/commercial-residential.webp",
};
const SECTOR_IMAGE_POSITIONS = {
  "Fire Protection": "center 24%",
  "Commercial & Residential": "center 45%",
};

export default function Home() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [news, setNews] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data.slice(0,6)));
    api.get("/projects").then(r => setProjects(r.data.slice(0,4)));
    api.get("/news").then(r => setNews(r.data.slice(0,3)));
    api.get("/branches").then(r => setBranches(r.data));
    api.get("/industries").then(r => setIndustries(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-0 md:min-h-[92vh] bg-pipeco-dark text-white overflow-hidden md:flex md:flex-col">
        {/* IMAGE — never distorted; positioned so tanks stay visible on every viewport */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="PIPECO GROUP water tank installation — GRP, HDG, GLS/GFS, Zinc Aluminium"
            className="hero-img w-full h-full object-cover select-none"
            draggable="false"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {/* Desktop: soft dark scrim on the left where text sits, image fully visible on the right */}
          <div className="hidden md:block absolute inset-0" style={{background:"linear-gradient(90deg, rgba(11,15,18,0.86) 0%, rgba(11,15,18,0.72) 30%, rgba(11,15,18,0.30) 55%, rgba(11,15,18,0) 78%)"}}/>
          {/* Mobile: gentle top-down darken so text on top is readable, tanks stay visible in the middle/bottom */}
          <div className="md:hidden absolute inset-0" style={{background:"linear-gradient(180deg, rgba(11,15,18,0.85) 0%, rgba(11,15,18,0.55) 45%, rgba(11,15,18,0.35) 65%, rgba(11,15,18,0.75) 100%)"}}/>
        </div>

        {/* CONTENT */}
        <div className="relative container-x px-5 md:px-10 pt-24 md:pt-40 pb-10 md:pb-24 md:flex-1 md:flex md:flex-col">
          <div className="overline text-emerald-300 text-[10px] md:text-[11px]" style={{textShadow:"0 1px 2px rgba(0,0,0,0.5)"}}>Welcome to the world of PIPECO</div>
          <h1 className="headline text-[34px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] mt-3 md:mt-5 max-w-5xl" style={{textShadow:"0 2px 12px rgba(0,0,0,0.55)"}}>
            Water Storage Solutions <span className="text-emerald-300">Beyond Borders.</span>
          </h1>
          <p className="text-white mt-4 md:mt-6 text-[13px] md:text-lg max-w-2xl md:max-w-3xl font-medium leading-relaxed" style={{textShadow:"0 1px 6px rgba(0,0,0,0.5)"}}>
            Manufacturing • Worldwide Supply<br className="sm:hidden"/> <span className="sm:hidden">Professional Installation • Warranty Support</span><span className="hidden sm:inline"> • Professional Installation • Warranty Support</span>
          </p>
          <div className="mt-2 md:mt-3 font-mono text-[9px] md:text-[11px] tracking-[0.2em] md:tracking-[0.3em] text-emerald-300/95 uppercase" style={{textShadow:"0 1px 4px rgba(0,0,0,0.6)"}}>
            GRP · HDG · GLS/GFS<br className="sm:hidden"/> <span className="sm:hidden">Zinc Aluminium · FRP &amp; Custom</span><span className="hidden sm:inline"> · Zinc Aluminium · FRP &amp; Custom</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-6 md:mt-10">
            <Link to="/products" data-testid="hero-explore-btn" className="btn-primary justify-center sm:justify-start w-full sm:w-auto shadow-xl shadow-black/50">
              Explore our Solutions <ArrowRight size={16}/>
            </Link>
            <a href={waLink(settings.whatsapp_number, quoteMessage())} target="_blank" rel="noreferrer" data-testid="hero-quote-btn" className="btn-whatsapp justify-center sm:justify-start w-full sm:w-auto shadow-xl shadow-black/50">
              <MessageCircle size={16}/>Get a Quote
            </a>
          </div>
          <div className="mt-8 md:mt-auto md:pt-8">
            <div className="grid grid-cols-3 gap-x-3 md:gap-6 max-w-4xl border-t border-white/25 pt-5 md:pt-8">
              {[
                ["10,000+","Projects"],
                ["26","Branches"],
                ["22","Countries"],
              ].map(([v,k]) => (
                <div key={k}>
                  <div className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-emerald-300 leading-none" style={{textShadow:"0 2px 8px rgba(0,0,0,0.5)"}}>{v}</div>
                  <div className="overline text-white mt-2 text-[9px] md:text-[11px] font-semibold" style={{textShadow:"0 1px 4px rgba(0,0,0,0.6)"}}>{k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-pipeco-green">About PIPECO GROUP</div>
            <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900">Engineering Water Storage Since 2010</h2>
          </div>
          <div className="lg:col-span-7 text-slate-700 leading-relaxed">
            <p>PIPECO GROUP has grown since 2010 through engineering experience, water storage expertise, multiple tank technologies, international project support, quality-focused manufacturing and long-term customer relationships.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div className="border-l-2 border-pipeco-green pl-5"><div className="overline text-pipeco-green mb-2">Our Mission</div><p className="text-sm">To provide high-performance water storage systems through engineering precision, manufacturing excellence, international quality compliance and end-to-end project support.</p></div>
              <div className="border-l-2 border-pipeco-green pl-5"><div className="overline text-pipeco-green mb-2">Our Vision</div><p className="text-sm">To become a trusted global leader in engineered water storage solutions through quality, innovation, reliability and customer-focused project support.</p></div>
            </div>
            <Link to="/about" className="btn-outline-dark mt-8" data-testid="about-more-btn">More about PIPECO <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section-pad bg-pipeco-neutral">
        <div className="container-x px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="overline text-pipeco-green">Our Water Tank Solutions</div>
              <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900">A Full Range of<br/>Engineered Tank Systems</h2>
            </div>
            <p className="text-slate-600 max-w-md">From GRP sectional to HDG steel, GLS/GFS and fully custom engineered tanks — one supplier for every water storage requirement.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(p => (
              <Link key={p.id} to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group bg-white card-lift block border border-black/5">
                <div className="aspect-4-5 overflow-hidden bg-slate-100 relative">
                  {p.main_image ? (
                    <img src={p.main_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  ) : (
                    <TankPlaceholder label={p.short_name || p.name} subtitle="Water Storage Solution" tone="dark"/>
                  )}
                  {p.tank_code && (
                    <div className="absolute top-4 left-4 bg-white/95 text-pipeco-green font-mono text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">
                      {p.tank_code}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="overline text-pipeco-green mb-2">Water Storage · Series {p.tank_code || ""}</div>
                  <h3 className="font-display text-2xl text-slate-900 leading-tight">{p.name}</h3>
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.intro}</p>
                  {p.chips && p.chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {p.chips.slice(0,3).map(ch => (<span key={ch} className="text-[10px] uppercase tracking-widest text-slate-600 bg-slate-100 px-2 py-1">{ch}</span>))}
                    </div>
                  )}
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[11px] tracking-widest uppercase text-slate-500 group-hover:text-pipeco-green transition-colors">View Product</span>
                    <ArrowUpRight className="text-pipeco-green group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20}/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MANUFACTURING */}
      <section className="section-pad bg-pipeco-dark text-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="overline text-emerald-300">Manufacturing Excellence</div>
            <h2 className="headline text-4xl md:text-5xl mt-3">Engineered.<br/>Precision-Built.</h2>
            <p className="text-white/70 mt-6 leading-relaxed max-w-lg">Every PIPECO tank starts on the engineering board and progresses through material preparation, panel manufacturing, quality control, testing and worldwide export logistics — under strict internal manufacturing controls.</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-8 max-w-md">
              {["Engineering","Material Preparation","GRP Panel Manufacturing","Steel Tank Manufacturing","Quality Control","Testing","Inspection","Worldwide Export"].map(x => (
                <div key={x} className="flex items-center gap-2 text-sm text-white/80"><ChevronRight size={12} className="text-emerald-300"/>{x}</div>
              ))}
            </div>
            <Link to="/manufacturing" data-testid="manufacturing-btn" className="btn-outline mt-8 border-emerald-300 text-emerald-300 hover:bg-emerald-300 hover:text-pipeco-dark">Discover our Facility <ArrowRight size={14}/></Link>
          </div>
          <div className="relative aspect-3-4 md:aspect-4-5 overflow-hidden">
            <img data-testid="manufacturing-image" src="/tanks/factory-pipeco.webp" alt="PIPECO GROUP manufacturing process and factory operations" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-pipeco-dark/60 via-transparent to-transparent"/>
          </div>
        </div>
      </section>

      {/* WHY PIPECO */}
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="overline text-pipeco-green">Why PIPECO</div>
          <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900 max-w-2xl">Engineered for Trust,<br/>Built for Every Project.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {[
              [Factory, "Manufacturing Excellence", "Panel and tank production under strict internal quality control procedures."],
              [Globe2, "Worldwide Supply", "Delivering engineered tank solutions to international projects across multiple regions."],
              [Wrench, "Professional Installation", "Trained PIPECO installation teams manage site assembly, testing and commissioning."],
              [LifeBuoy, "Warranty & After-Sales", "Long-term product warranty and dedicated after-sales support for every project."],
            ].map(([Ic,t,d]) => (
              <div key={t} className="border-t-2 border-pipeco-green pt-6">
                <Ic className="text-pipeco-green" size={26}/>
                <h3 className="font-display text-xl mt-4 text-slate-900">{t}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section-pad bg-pipeco-neutral">
        <div className="container-x px-6 md:px-10">
          <div className="overline text-pipeco-green">Industries We Serve</div>
          <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900">Trusted Across Sectors</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-10">
            {industries.map(ind => {
              const sectorImage = SECTOR_IMAGE_OVERRIDES[ind.name];
              return (
              <Link key={ind.id} to="/industries" data-testid={`industry-${ind.slug}`} className="relative group h-44 overflow-hidden bg-pipeco-dark">
                {sectorImage ? (
                  <>
                    <img data-testid={`industry-photo-${ind.slug}`} src={sectorImage} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-55 group-hover:scale-110 transition-all duration-500" style={{objectPosition: SECTOR_IMAGE_POSITIONS[ind.name] || "center"}}/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"/>
                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                      <div className="overline text-emerald-300 text-[9px] mb-1">Industry</div>
                      <div className="font-display uppercase font-bold text-lg text-white leading-tight">{ind.name}</div>
                      <ArrowUpRight className="text-white/80 mt-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={16}/>
                    </div>
                  </>
                ) : ind.image ? (
                  <>
                    <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-40 group-hover:scale-110 transition-all duration-500"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent"/>
                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                      <div className="font-display uppercase font-bold text-lg text-white leading-tight">{ind.name}</div>
                      <ArrowUpRight className="text-white/80 mt-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={16}/>
                    </div>
                  </>
                ) : (
                  <>
                    <TankPlaceholder label={ind.name} subtitle="Industry" tone="dark"/>
                    <div className="absolute bottom-4 right-4"><ArrowUpRight className="text-emerald-300 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={16}/></div>
                  </>
                )}
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline text-pipeco-green">Featured Projects</div>
              <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900">Delivered Worldwide</h2>
            </div>
            <Link to="/projects" className="btn-outline-dark" data-testid="all-projects-btn">All projects <ArrowRight size={14}/></Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {projects.map((pr, i) => (
              <Link key={pr.id} to={`/projects/${pr.id}`} className={`relative group overflow-hidden ${i%2===0?"md:aspect-4-5":"md:aspect-3-4"} aspect-4-5 bg-pipeco-dark`}>
                {pr.main_image ? (
                  <img src={pr.main_image} alt={pr.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                ) : (
                  <TankPlaceholder label={pr.tank_type} subtitle={`${pr.country} · ${pr.capacity}`} tone="dark"/>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"/>
                <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                  <div className="overline text-emerald-300">{pr.country} · {pr.tank_type}</div>
                  <h3 className="headline text-3xl mt-2">{pr.name}</h3>
                  <div className="mt-3 flex items-center gap-4 text-xs text-white/70 uppercase tracking-widest">
                    <span>{pr.capacity}</span><span>·</span><span>{pr.status}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE */}
      <section className="home-global-presence-section section-pad bg-pipeco-dark text-white">
        <div className="container-x px-6 md:px-10">
          <div className="home-global-presence-header flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline text-emerald-300">Global Presence</div>
              <h2 className="home-global-presence-heading headline text-4xl md:text-5xl mt-3">One Group.<br className="home-global-presence-break"/>Every Region.</h2>
              <div className="home-global-presence-stats mt-5 flex flex-wrap gap-6 md:gap-10">
                <div><div className="font-display text-3xl md:text-4xl font-black text-emerald-300 leading-none">26</div><div className="overline text-white/50 mt-1 text-[10px]">Branches</div></div>
                <div><div className="font-display text-3xl md:text-4xl font-black text-emerald-300 leading-none">22</div><div className="overline text-white/50 mt-1 text-[10px]">Countries</div></div>
                <div><div className="font-display text-3xl md:text-4xl font-black text-emerald-300 leading-none">10K+</div><div className="overline text-white/50 mt-1 text-[10px]">Projects</div></div>
                <div><div className="font-display text-3xl md:text-4xl font-black text-emerald-300 leading-none">2010</div><div className="overline text-white/50 mt-1 text-[10px]">Since</div></div>
              </div>
            </div>
            <Link to="/global-presence" className="home-global-presence-button btn-outline border-white/50" data-testid="global-presence-btn">View all offices <ArrowRight size={14}/></Link>
          </div>
          <div className="home-map-container">
            <WorldMap branches={branches} globalPresenceMap homePreview interactive={false}/>
          </div>
          {selectedBranch && <BranchDetailCard branch={selectedBranch} onClose={()=>setSelectedBranch(null)}/>}
        </div>
      </section>

      {/* CLIENTS */}
      <ClientsStrip/>

      {/* QUALITY & WARRANTY */}
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 gap-8">
          <Link to="/quality" data-testid="home-quality-card" className="p-10 bg-pipeco-neutral border border-black/5 card-lift">
            <Award className="text-pipeco-green" size={28}/>
            <div className="overline text-pipeco-green mt-4">Quality & Certifications</div>
            <h3 className="headline text-3xl md:text-4xl mt-3 text-slate-900">Compliance you can<br/>engineer around.</h3>
            <p className="text-slate-600 mt-4 max-w-md">Material inspection, manufacturing standards, testing, traceability and engineering quality — with real certifications uploadable via the admin panel.</p>
            <div className="mt-6 flex items-center gap-2 overline text-pipeco-green">Explore <ArrowRight size={14}/></div>
          </Link>
          <Link to="/warranty" data-testid="home-warranty-card" className="p-10 bg-pipeco-dark text-white card-lift">
            <ShieldCheck className="text-emerald-300" size={28}/>
            <div className="overline text-emerald-300 mt-4">Warranty & After-Sales</div>
            <h3 className="headline text-3xl md:text-4xl mt-3">Support that outlasts<br/>the project handover.</h3>
            <p className="text-white/70 mt-4 max-w-md">Product-specific warranty terms and long-term after-sales support with dedicated service teams.</p>
            <div className="mt-6 flex items-center gap-2 overline text-emerald-300">Explore <ArrowRight size={14}/></div>
          </Link>
        </div>
      </section>

      {/* RESOURCES & NEWS */}
      <section className="section-pad bg-pipeco-neutral">
        <div className="container-x px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline text-pipeco-green">Latest News</div>
              <h2 className="headline text-4xl md:text-5xl mt-3 text-slate-900">Announcements & Updates</h2>
            </div>
            <Link to="/news" className="btn-outline-dark" data-testid="all-news-btn">All news <ArrowRight size={14}/></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {news.map(n => (
              <Link key={n.id} to="/news" className="group bg-white card-lift block">
                <div className="aspect-16-9 overflow-hidden bg-pipeco-green">
                  {n.featured_image
                    ? <img src={n.featured_image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    : <TankPlaceholder label={n.category} subtitle="PIPECO News" tone="dark"/>}
                </div>
                <div className="p-6">
                  <div className="overline text-pipeco-green">{n.category}</div>
                  <h3 className="font-display text-xl mt-2 text-slate-900 leading-tight">{n.title}</h3>
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{n.content}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 bg-pipeco-green text-white overflow-hidden">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="overline text-emerald-200">Project Enquiry</div>
            <h2 className="headline text-4xl md:text-6xl mt-3">Ready to specify<br/>your water storage?</h2>
            <p className="text-white/85 mt-5 max-w-lg">Send us your project brief, drawings or BOQ. A PIPECO project engineer will respond with a tailored technical proposal.</p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <a data-testid="final-whatsapp-cta" href={waLink(settings.whatsapp_number, quoteMessage())} target="_blank" rel="noreferrer" className="btn-whatsapp justify-center"><MessageCircle size={16}/>WhatsApp us Now</a>
            <Link to="/contact" data-testid="final-enquire-cta" className="btn-outline justify-center border-white text-white hover:bg-white hover:text-pipeco-green">Send Project Enquiry <ArrowRight size={14}/></Link>
            <a href={`tel:${settings.whatsapp_number}`} className="btn-outline justify-center border-white/40 text-white/90 hover:bg-white/10 hover:!text-white"><PhoneCall size={16}/>Call PIPECO</a>
          </div>
        </div>
      </section>
    </div>
  );
}
